import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { AlertTriangle, ArrowRight, CheckCircle2, ExternalLink, Loader2, Send, Clock } from "lucide-react";
import { getBackendUrl } from "../../lib/http";
import { EtsyPreview } from "./EtsyPreview";
import { apiClient } from "../../lib/api";
import { customApi, type CustomListingDetail } from "../../lib/customListing";

// Custom Liste Studyosu 7. adim: yayin. Panel taslagi Etsy liste sayfasi gibi gosterilir
// (taslagin GERCEK verisi: gorseller, fiyat, varyasyonlar, kisisellestirme, aciklama,
// etiketler). "Etsy'ye taslak gonder" yayin kuyruguna is acar (yalniz Etsy TASLAGI);
// yayina alma (ucretli) Etsy'de ya da Listing Builder'da kullanici onayiyla.

interface PublishState {
  etsyListingId: string | null;
  blockers: string[];
  warnings: string[];
  job: { id: string; status: "pending" | "processing" | "done" | "error"; error: string | null; etsyListingId: string | null } | null;
}
const JOB_LABEL: Record<string, string> = { pending: "Kuyrukta bekliyor", processing: "Etsy'ye gönderiliyor", done: "Etsy taslağı oluşturuldu", error: "Gönderim başarısız" };

const abs = (u: string) => (/^(https?:|data:)/.test(u) ? u : `${getBackendUrl()}${u.startsWith("/") ? "" : "/"}${u}`);
const STATUS_LABEL: Record<string, string> = { draft: "Panel taslağı", ready: "Hazır", scheduled: "Planlandı", published: "Etsy'de yayında", archived: "Arşivde" };

/** 7. adim: ana taslak + 6. adimda nis ailesiyle cogaltilan kardes taslaklar (sekmeli) */
export function PublishStep({ rec, onChanged }: { rec: CustomListingDetail; onChanged: () => void }) {
  const niches = rec.template.niches?.items ?? [];
  const family = [
    ...(rec.links.draftId ? [{ id: rec.links.draftId, label: "Ana liste", values: null as Record<string, string> | null }] : []),
    ...niches.filter((n) => n.draftId).map((n) => ({ id: n.draftId!, label: n.niche, values: n.values })),
  ];
  const [sel, setSel] = useState<string | null>(null);
  const current = family.find((f) => f.id === sel) ?? family[0];
  const pending = niches.filter((n) => !n.draftId && n.status === "running").length;
  if (!current) return <p className="text-sm text-gray-600">Önce 6. adımda panel taslağını oluştur; burada Etsy'de nasıl görüneceğini kontrol edip yayınlarsın.</p>;
  return (
    <div className="space-y-4">
      {family.length > 1 && (
        <div>
          <p className="mb-1.5 text-xs font-medium text-gray-600">Liste ailesi ({family.length}) · aynı tasarım yapısı, farklı niş{pending ? ` · ${pending} taslak kuruluyor` : ""}</p>
          <div role="tablist" aria-label="Liste ailesi" className="flex flex-wrap gap-1.5">
            {family.map((f) => (
              <button key={f.id} role="tab" aria-selected={f.id === current.id} onClick={() => setSel(f.id)}
                className={clsx("rounded-lg border px-3 py-1.5 text-xs font-medium", f.id === current.id ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50")}>
                {f.label}
              </button>
            ))}
          </div>
          {current.values && Object.keys(current.values).length > 0 && (
            <p className="mt-1.5 text-xs text-gray-500">Niş metni: {Object.values(current.values).join(" · ")}</p>
          )}
        </div>
      )}
      <DraftPanel key={current.id} rec={rec} draftId={current.id} isMain={current.id === rec.links.draftId} onChanged={onChanged} />
    </div>
  );
}

function DraftPanel({ rec, draftId, isMain, onChanged }: { rec: CustomListingDetail; draftId: string; isMain: boolean; onChanged: () => void }) {
  const q = isMain ? "" : `?draft=${encodeURIComponent(draftId)}`;
  const { data: d, isLoading, error, refetch: refetchDraft } = useQuery({
    queryKey: ["listing-draft", draftId], enabled: !!draftId,
    queryFn: () => apiClient.getListingDraft(draftId!),
  });
  const pub = useQuery({
    queryKey: ["custom-publish", rec.id, draftId], enabled: !!draftId,
    queryFn: () => customApi<PublishState>("GET", `/${rec.id}/publish${q}`),
    // Is surerken yoklanir; bitince durur
    refetchInterval: (q) => (["pending", "processing"].includes(q.state.data?.job?.status ?? "") ? 4000 : false),
  });
  const send = useMutation({
    mutationFn: () => customApi("POST", `/${rec.id}/publish${q}`),
    onSuccess: () => void pub.refetch(),
  });
  const jobStatus = pub.data?.job?.status;
  // Is bitince adim durumu ve taslak (etsyListingId) tazelenir
  useEffect(() => {
    if (jobStatus === "done") { if (isMain) onChanged(); void refetchDraft(); }
  }, [jobStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!draftId) return <p className="text-sm text-gray-600">Önce 6. adımda panel taslağını oluştur; burada Etsy'de nasıl görüneceğini kontrol edip yayınlarsın.</p>;
  if (isLoading) return <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Taslak yükleniyor…</p>;
  if (error || !d) return <p className="text-sm text-red-600">{(error as Error)?.message ?? "Taslak bulunamadı"}</p>;

  const images = (d.imageUrls ?? []).map(abs);
  const questions = d.personalization?.enabled ? d.personalization.questions ?? [] : [];
  const prices = (d.variations ?? []).flatMap((v) => v.options.map((o) => o.sellingPrice).filter((p): p is number => typeof p === "number"));
  const price = prices.length ? Math.min(...prices) : d.price;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">{STATUS_LABEL[d.status] ?? d.status}</span>
        <span className="text-xs text-gray-500">Etsy'de görüneceği hali; değişiklik Listing Builder'da yapılır.</span>
      </div>

      <EtsyPreview data={{
        images, title: d.title, price: price ?? null, priceFrom: prices.length > 1,
        variations: (d.variations ?? []).map((v) => ({ name: v.name, options: v.options.map((o) => o.label) })),
        questions: questions.map((q) => ({ type: q.question_type, text: q.question_text, instructions: q.instructions, required: !!q.required, options: q.options?.map((o) => o.label), maxChars: q.max_allowed_characters })),
        description: d.description ?? "", tags: d.tags ?? [],
      }} />

      {pub.data && (
        <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm font-semibold text-gray-900">Etsy'ye gönder</p>
          <p className="text-xs text-gray-600">Liste Etsy'de <b>taslak</b> olarak açılır, kişiselleştirme soruları yazılıp geri okunarak doğrulanır. Yayına alma (ücretli) Etsy'de ya da Listing Builder'da senin onayınla yapılır.</p>
          {pub.data.blockers.length > 0 && (
            <ul className="space-y-0.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              {pub.data.blockers.map((b) => <li key={b} className="flex items-start gap-1.5 text-xs text-red-700"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {b}</li>)}
            </ul>
          )}
          {pub.data.warnings.length > 0 && (
            <ul className="space-y-0.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              {pub.data.warnings.map((w) => <li key={w} className="flex items-start gap-1.5 text-xs text-amber-800"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {w}</li>)}
            </ul>
          )}
          {pub.data.job && (
            <p className={clsx("flex items-center gap-1.5 text-xs", jobStatus === "error" ? "text-red-600" : jobStatus === "done" ? "text-emerald-700" : "text-gray-600")}>
              {jobStatus === "done" ? <CheckCircle2 className="h-3.5 w-3.5" /> : jobStatus === "error" ? <AlertTriangle className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {JOB_LABEL[jobStatus ?? ""] ?? jobStatus}{jobStatus === "error" && pub.data.job.error ? `: ${pub.data.job.error}` : ""}
            </p>
          )}
          {send.error && <p className="text-xs text-red-600">{(send.error as Error).message}</p>}
          {!pub.data.etsyListingId && (
            <button onClick={() => send.mutate()}
              disabled={send.isPending || pub.data.blockers.length > 0 || jobStatus === "pending" || jobStatus === "processing"}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {jobStatus === "error" ? "Tekrar gönder" : "Etsy'ye taslak olarak gönder"}
            </button>
          )}
        </div>
      )}
      {pub.error && <p className="text-xs text-red-600">{(pub.error as Error).message}</p>}

      <div className="flex flex-wrap gap-2">
        <Link to={`/listing-builder?id=${draftId}`} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Listing Builder'da düzenle ve yayınla <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/scheduler" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><Clock className="h-4 w-4" /> Zamanlayıcı</Link>
        {d.etsyListingId && (
          <a href={`https://www.etsy.com/listing/${d.etsyListingId}`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Etsy'de aç <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}
